import { api, type PostingIdentity } from "../lib/api";
import { useState } from "react";
import { Link } from "wouter";
import { Star } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { AccountVerificationBadge } from "./AccountVerificationBadge";
import { Verified } from "./ui/kit";

type ListingProfileCardProps = {
  postingIdentity?: PostingIdentity;
  title: string;
  profile: {
    name: string;
    role: string;
    location?: string | null;
    memberSince?: string | null;
    imageUrl?: string | null;
    href?: string | null;
    identityVerified?: boolean;
    companyVerified?: boolean;
    verifiedProfessions?: readonly string[];
  };
  statistics: { label: string; value: number | null | undefined }[];
  rating?: { average: number | null; count: number } | null;
  description?: string | null;
};

function ProfileAvatar({ name, imageUrl }: { name: string; imageUrl?: string | null }) {
  const [failed, setFailed] = useState(false);
  return <div className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-full bg-slate-400 text-xl text-white max-sm:h-[38px] max-sm:w-[38px] max-sm:bg-navy-50 max-sm:text-sm max-sm:font-bold max-sm:text-navy">
    {imageUrl && !failed ? <img src={imageUrl} alt="" className="h-full w-full object-cover" onError={() => setFailed(true)} /> : name.slice(0, 1).toUpperCase()}
  </div>;
}

/** Shared public identity/history presentation; callers retain category and visibility policy. */
export function ListingProfileCard({ title, profile: originalProfile, statistics, rating, description, postingIdentity }: ListingProfileCardProps) {
  const business = postingIdentity?.kind === "business" ? postingIdentity : null;
  const profile = business ? { name: business.name, role: "Business", location: originalProfile.location, href: business.href, imageUrl: api.apiAssetUrl(business.imageUrl) } : originalProfile;
  const { t } = useI18n();
  const memberYear = profile.memberSince ? new Date(profile.memberSince).getFullYear() : NaN;
  const contents = <>
    <h2 className="font-bold text-navy max-sm:text-lg">{title}</h2>
    <div className="mt-4 flex items-center gap-3">
      <ProfileAvatar key={profile.imageUrl || profile.name} name={profile.name} imageUrl={profile.imageUrl} />
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-1.5 text-sm font-semibold text-navy transition-colors motion-reduce:transition-none group-hover:text-blue-700 group-focus-visible:text-blue-700">
          <span className="min-w-0 truncate" title={profile.name}>{profile.name}</span>
          {profile.companyVerified ? <Verified label="Verified Company" iconOnly /> : <AccountVerificationBadge identityVerified={profile.identityVerified} verifiedProfessions={profile.verifiedProfessions} />}
        </div>
        <p className="text-xs text-mut">{profile.role} · {profile.location || t("detail.item.locationUnavailable")}</p>
        {!business && <p className="mt-1 text-xs text-mut">{Number.isFinite(memberYear) ? `${t("detail.item.memberSince")} ${memberYear}` : t("detail.item.memberSinceUnavailable")}</p>}
      </div>
    </div>
    {!business && description && <p className="mt-4 text-sm leading-relaxed text-mut">{description}</p>}
    {!business && <div className="mt-4 grid auto-cols-fr grid-flow-col divide-x divide-line border-y border-line py-3 text-center">
      {statistics.map(({ label, value }) => <div key={label} className="min-w-0 px-1"><strong className="block text-base text-navy">{value ?? "—"}</strong><span className="text-[10px] text-mut">{label}</span></div>)}
      {rating !== undefined && <div className="min-w-0 px-1">
        <strong className="block text-base text-navy">{rating && rating.count > 0 && rating.average != null ? <>{rating.average.toFixed(1)} <Star aria-hidden="true" className="inline h-3 w-3 fill-brand-gold text-brand-gold" /></> : "—"}</strong>
        <span className="text-[10px] text-mut">{rating ? `${rating.count} ${t("detail.item.reviews")}` : t("detail.profile.reviewsUnavailable")}</span>
      </div>}
    </div>}
  </>;
  return profile.href ? <Link href={profile.href} aria-label={`${t("detail.item.viewProfile")} ${profile.name}`}
    className="card group block p-5 transition-colors motion-reduce:transition-none max-sm:rounded-none max-sm:border-x-0 max-sm:border-t-0 max-sm:shadow-none hover:border-blue-300 hover:bg-blue-50/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600">{contents}</Link>
    : <section className="card p-5 max-sm:rounded-none max-sm:border-x-0 max-sm:border-t-0 max-sm:shadow-none">{contents}</section>;
}
